import type { SvgIconComponent } from '@mui/icons-material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined'
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'

import { poolwerxColors } from '../../../theme/colors'

/**
 * The single taxonomy of modal-header variants.
 *
 * Every modal header picks a `variant` (never a raw icon), so each one resolves
 * to one icon + colour rendered by `DialogHeader` inside a soft tinted badge.
 * This is what keeps the headers configured the same way - there are no bespoke
 * per-modal icons. Add a variant here when a genuinely new semantic appears;
 * don't pass a one-off icon at the call site.
 */
export type ModalVariant =
  | 'info'
  | 'question'
  | 'alert'
  | 'confirmation'
  | 'edit'
  | 'destructive'
  | 'sync'
  | 'announcement'

export interface ModalVariantSpec {
  icon: SvgIconComponent
  /** Icon colour. The badge background is this colour at 12% alpha. */
  color: string
}

export const MODAL_VARIANTS: Record<ModalVariant, ModalVariantSpec> = {
  info: { icon: InfoOutlinedIcon, color: poolwerxColors.darkCyan },
  question: { icon: HelpOutlineOutlinedIcon, color: poolwerxColors.variantSlate },
  alert: { icon: WarningAmberOutlinedIcon, color: poolwerxColors.variantAmber },
  confirmation: { icon: CheckCircleOutlinedIcon, color: poolwerxColors.variantGreen },
  edit: { icon: EditOutlinedIcon, color: poolwerxColors.darkCyan },
  destructive: { icon: ErrorOutlineOutlinedIcon, color: poolwerxColors.variantRed },
  sync: { icon: SyncOutlinedIcon, color: poolwerxColors.teal },
  announcement: { icon: CampaignOutlinedIcon, color: poolwerxColors.teal },
}

/**
 * The single source of truth for a dialog's primary-button colour, shared by
 * `ConfirmDialog` and `DialogFooter` so the two can never drift apart.
 *
 * `neutral` -> `primary`, which the MUI theme paints Poolwerx teal
 * (`palette.primary.main === #007f98`) - so the default footer button is teal
 * with no custom `sx`. `warning`/`danger` map to the standard MUI palette slots.
 */
export type DialogTone = 'neutral' | 'warning' | 'danger'

export const TONE_BUTTON_COLOR: Record<DialogTone, 'primary' | 'warning' | 'error'> = {
  neutral: 'primary', // teal - DEFAULT (info / edit / sync / announcement)
  warning: 'warning', // amber
  danger: 'error', // red
}
